import { formatUTCDateISO, pickAndRename } from 'common/utilities/MiscHelpers';
import { groupBy, uniqBy } from 'lodash';
import type { BreachedDetail, SingleBreachEntityField } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import {
  Breach,
  BreachEntity,
  BreachEntityGroup,
  BreachEntityGroupType,
  MultiEntityGroupType,
  SingleValueBreachEntityType,
} from './breach.types';

// Helper type for specifying breachType of SingleBreachEntityField
type SingleBreachEntityFieldOfType<T> = SingleBreachEntityField & { breachType: T };

/**
 * Alias type for map from {@link SingleValueBreachEntityType} to list of {@link SingleBreachEntityField} or `undefined`
 */
type BreachFieldLookup = {
  [type in SingleValueBreachEntityType]: [SingleBreachEntityFieldOfType<type>, ...SingleBreachEntityFieldOfType<type>[]] | undefined;
};

/**
 * Transform a {@link SingleBreachEntityField} to a {@link BreachEntity}
 *
 * @param field
 */
const fieldToEntity = <T extends SingleValueBreachEntityType>(
  field: SingleBreachEntityFieldOfType<T> | undefined
): BreachEntity<T> | undefined =>
  field
    ? {
        // non-null assertions because they are guaranteed to be present by API
        entityId: field.entityId!,
        entityState: field.state!,
        entityType: field.breachType!,
        value: field.value!,
        lastModifiedDate: field.lastModifiedDate,
      }
    : undefined;

/**
 * Pick out all {@link SingleBreachEntityField} of type {@link entityType} from the field lookup ({@link fields}) and
 * transform them into {@link BreachEntity} then tag the result with {@link groupType} to form a {@link BreachEntityGroup}
 *
 * @param fields
 * @param groupType
 * @param entityType
 */
const pickEntity = <GT extends BreachEntityGroupType, ET extends SingleValueBreachEntityType>(
  fields: BreachFieldLookup,
  groupType: GT,
  entityType: ET
): (BreachEntity<ET> & { groupType: GT })[] => {
  const entityGroups: (BreachEntity<ET> & { groupType: GT })[] = [];

  // Consume until there is no more of this entity type
  for (let i = 0; i < Infinity; i++) {
    const entity = fieldToEntity((fields[entityType] ?? [])[i]);

    if (!entity) {
      break;
    }

    entityGroups.push({ ...entity, groupType } as BreachEntity<ET> & { groupType: GT });
  }

  return entityGroups;
};

/**
 * Pick out multiple {@link SingleBreachEntityField} from the field lookup ({@link fields}) and transform them into {@link BreachEntity}
 * then tag the result with {@link groupType} to form a {@link BreachEntityGroup}
 *
 * @param fields
 * @param groupType
 * @param keyMap - See `keyMap` param in {@link pickAndRename}
 */
const pickEntities = <GT extends MultiEntityGroupType, KeyMap extends Record<string, SingleValueBreachEntityType>>(
  fields: BreachFieldLookup,
  groupType: GT,
  keyMap: KeyMap
): ({ [k in keyof KeyMap]: BreachEntity<KeyMap[k]> | undefined } & { groupType: GT })[] => {
  const entityGroups: ({ [k in keyof KeyMap]: BreachEntity<KeyMap[k]> | undefined } & { groupType: GT })[] = [];

  // Consume all available entities of types specified in `keyMap`
  for (let i = 0; i < Infinity; i++) {
    // Construct an entity group from the entity types in `keyMap`
    const entityGroup = {} as {
      [k in keyof KeyMap]: BreachEntity<KeyMap[k]> | undefined;
    };

    // For each key/type, get entity obtained from `fields`
    Object.keys(keyMap).forEach((k: keyof KeyMap) => {
      const field = (fields[keyMap[k]] ?? [])[i];
      const entity = fieldToEntity(field) as BreachEntity<KeyMap[keyof KeyMap]> | undefined;
      entityGroup[k] = entity;
    });

    // If there is no value in the entity group, stop consuming
    if (!Object.values(entityGroup).some(Boolean)) {
      break;
    }

    // If there is some value in the entity group, add it to `entityGroups`
    entityGroups.push({ ...entityGroup, groupType });
  }

  return entityGroups;
};

/**
 * Extract and construct list of {@link BreachEntityGroup} from a {@link BreachDetail}
 *
 * @param breachDetail
 */
const extractBreachEntityGroups = (breachDetail: BreachedDetail): BreachEntityGroup[] => {
  const fields = groupBy(breachDetail.singleBreachEntities ?? [], (entity) => entity.breachType) as BreachFieldLookup;

  // single-entity entity groups
  const ssn = pickEntity(fields, 'SSN', 'SocialSecurityNumber');
  const iban = pickEntity(fields, 'IBAN', 'InternationalBankAccountNumber');
  const email = pickEntity(fields, 'Email', 'EmailAddress');
  const cellPhone = pickEntity(fields, 'CellPhone', 'CellPhone');
  const homePhone = pickEntity(fields, 'HomePhone', 'HomePhone');
  const workPhone = pickEntity(fields, 'WorkPhone', 'WorkPhone');
  const retailCard = pickEntity(fields, 'RetailCard', 'RetailCardNumber');
  const userId = pickEntity(fields, 'UserId', 'UserId');
  const password = pickEntity(fields, 'Password', 'Password');
  const mothersMaidenName = pickEntity(fields, 'MotherMaidenName', 'MothersMaidenName');
  const dateOfBirth = pickEntity(fields, 'DateOfBirth', 'DateOfBirth');

  // multi-entity entity groups
  // TODO: for multi-entity entity groups, group `singleBreachEntities` by breach record instead (requires API changes)
  const name = pickEntities(fields, 'Name', {
    firstName: 'FirstName',
    middleName: 'MiddleName',
    lastName: 'LastName',
  });
  const ebayCredentials = pickEntities(fields, 'EbayCredentials', {
    userId: 'EbayUserId',
    password: 'EbayPassword',
  });
  const payPalCredentials = pickEntities(fields, 'PayPalCredentials', {
    userId: 'PaypalUserId',
    password: 'PaypalPassword',
  });
  const phone = pickEntities(fields, 'Phone', {
    phoneNumber: 'Phone',
    extension: 'PhoneExt',
  });
  const medicalId = pickEntities(fields, 'MedicalId', {
    id: 'MedicalId',
    provider: 'MedicalProvider',
  });
  const creditCard = pickEntities(fields, 'CreditCard', {
    cardNumber: 'CreditCard',
    cardType: 'Cardtype',
    cardExpYear: 'CardExpYear',
    cardExpMonth: 'CardExpMonth',
    cardExpDay: 'CardExpDay',
    expiryDate: 'CardExpDate',
    cvn: 'CreditCardCVN',
    pin: 'CreditCardPin',
    holderName: 'CardName',
    holderAddress: 'CardAddr',
  });
  const bankAccount = pickEntities(fields, 'BankAccount', {
    accountNumber: 'BankAccountNumber',
    routingNumber: 'BankRoute',
    pin: 'BankingPin',
    userId: 'BankUserId',
    password: 'BankingPassword',
    bankName: 'BankName',
    bankPhone: 'BankPhone',
  });
  const driverLicense = pickEntities(fields, 'DriverLicense', {
    number: 'DriverLicenseNumber',
    state: 'DriversLicenseState',
  });
  const passport = pickEntities(fields, 'Passport', {
    number: 'PassportNumber',
    countryOrRegion: 'PassportCountry',
    expiryDate: 'PassportExpDate',
    issueDate: 'PassportIssueDate',
  });
  const nationalId = pickEntities(fields, 'NationalId', {
    id: 'NationalId',
    countryOrRegion: 'NationalCountry',
  });

  // "composite" breach entities
  const compositeEntities =
    breachDetail.compositeBreachEntities?.map((entity): BreachEntityGroup | undefined => {
      switch (entity.breachType) {
        case 'Address':
          return {
            groupType: 'Address',
            entityId: entity.entityId!,
            entityState: entity.state!,
            entityType: entity.breachType!,
            ...pickAndRename(
              entity.address!, // as promised by API that `address` will be present when type is `Address`
              {
                line1: 'addr1',
                line2: 'addr2',
                city: 'addrCity',
                state: 'addrstate',
                countryOrRegion: 'addrCountry',
                zip: 'addrZip',
              }
            ),
            lastModifiedDate: entity.lastModifiedDate,
          };
        default:
          // This should not happen as only Address is a composite entity
          return undefined;
      }
    }) ?? [];

  return compositeEntities
    .concat(
      [
        ssn,
        iban,
        email,
        cellPhone,
        homePhone,
        workPhone,
        retailCard,
        userId,
        password,
        mothersMaidenName,
        dateOfBirth,
        name,
        ebayCredentials,
        payPalCredentials,
        phone,
        medicalId,
        creditCard,
        bankAccount,
        driverLicense,
        passport,
        nationalId,
      ].flat()
    )
    .filter((data): data is BreachEntityGroup => data !== undefined);
};

/**
 * Build {@link Breach} array from {@link BreachedDetail} (API response).
 *
 * @param breachDetails Response received from breach API
 */
export const buildBreaches = (breachDetails: BreachedDetail[]): Breach[] =>
  breachDetails.map((breachDetail): Breach => {
    // TODO: remove non-null assertions
    return {
      breachAlertId: breachDetail.identityMonitoringBreachAlertId!,
      breachId: breachDetail.breachId!,
      site: breachDetail.site,
      siteFaviconUrl: breachDetail.siteFaviconUrl,
      isKnownSite: breachDetail.isKnownSite,
      changePasswordUrl: breachDetail.changePasswordUrl,
      breachEntityGroups: extractBreachEntityGroups(breachDetail),
      recommendations: (breachDetail.recommendations ?? []).map((rec) => ({
        recommendationId: rec.recommendationId!,
        breachEntityId: rec.breachEntityId!,
        isCompleted: rec.isCompleted!,
      })),
      breachRecommendations: breachDetail.breachRecommendations ?? [],
      foundDate: formatUTCDateISO(breachDetail.dateTimeOfBreach),
      source: breachDetail.source,
      breachStateApiStatus: ApiCallResultStatus.DEFAULT,
    };
  });

/**
 * Build {@link Breach} array from {@link newBreaches} and combine the results with {@link existingBreaches},
 * with duplicate (by {@link Breach.breachId}) breaches in {@link existingBreaches} removed.
 *
 * @param existingBreaches Existing breaches
 * @param newBreaches New breaches (scan results)
 */
export const mergeBreaches = (existingBreaches: Breach[], newBreaches: BreachedDetail[]): Breach[] =>
  uniqBy([...buildBreaches(newBreaches), ...existingBreaches], 'breachId');
